import React from "react";
import { Button, message } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCopy } from "@fortawesome/free-solid-svg-icons";
import { UuidT } from "../../flatbuffers/generated/front-end/uuid";
import { formatUuidT } from "../../flatbuffers/display";
import FormInputText from "../components/form/FormInputText";
import { FormularyType } from "../../interfaces/Wizard.interface";

interface WizardReadOnlyIdProps {
  /** Field label, e.g. "Adapter ID", "Bank ID". */
  label: string;
  /**
   * The entity's id. Pass `undefined`/`null` (not an unrelated id, e.g. a
   * clone source) when the entity being edited has no saved id yet -- by
   * default the field renders nothing in that case (see `emptyText` to
   * change this).
   */
  value: UuidT | string | number | null | undefined;
  /**
   * When set, the field is always shown even if `value` is empty, displaying
   * this text in place of the id (and omitting the copy button, since
   * there's nothing to copy). Use this for fields where an empty value is
   * itself meaningful information -- e.g. "no linked Cognito identity" is a
   * fact about the user, not a loading/not-yet-saved state -- as opposed to
   * the default hide-when-empty behavior, which is for entities that simply
   * don't have an id yet (not yet saved, or a create-mode form).
   */
  emptyText?: string;
}

/**
 * Read-only id field + copy-to-clipboard button, shown once an entity has
 * been saved and has a real backend-assigned id. Mirrors the pattern
 * StepServer.tsx established for the server id field, factored out so
 * Adapter/Bank/Lift/Notification editors can share it instead of each
 * re-implementing the same block.
 */
export default function WizardReadOnlyId({
  label,
  value,
  emptyText = undefined,
}: WizardReadOnlyIdProps) {
  const idString = formatUuidT(value);
  if (!idString && !emptyText) return null;
  const displayValue = idString || emptyText || "";

  return (
    <div className="container" style={{ marginBottom: "15px" }}>
      <div className="row">
        <div className="col-12 col-md-12">
          <div style={{ display: "flex", gap: "8px", alignItems: "flex-end" }}>
            <div style={{ flex: 1 }}>
              <FormInputText
                inputValue={displayValue}
                inputLabelName={label}
                inputOnChange={() => {}}
                inputVariableName="id"
                isRequired
                disabledInput
                formularyType={FormularyType.WIZARD}
              />
            </div>
            {idString && (
              <Button
                type="default"
                size="middle"
                icon={<FontAwesomeIcon icon={faCopy} />}
                onClick={() => {
                  navigator.clipboard.writeText(idString);
                  message.success(`${label} copied to clipboard`);
                }}
                style={{ marginBottom: "4px" }}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
